/**
 * Sprint Header Component
 * Displays sprint info with active indicator (green light) like monday.com
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  LinearProgress,
  Tooltip,
  Select,
  FormControl,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TimelineIcon from '@mui/icons-material/Timeline';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import {
  startSprint,
  resetSprint,
  deleteSprint,
  fetchSprints,
} from '../../store/sprintSlice';
import { fetchTasks } from '../../store/taskSlice';
import { showToast } from '../../store/uiSlice';
import { Sprint } from '../../entities/sprint/model/types';
import dayjs from 'dayjs';
import './SprintHeader.scss';

interface SprintHeaderProps {
  sprint: Sprint;
  allSprints?: Sprint[];
  /**
   * Whether the caller can manage this sprint (start/complete/reset/delete)
   * - mirrors the backend's BOARD_MANAGE permission gate on the /sprints/*
   * endpoints. Defaults to true so existing callers that haven't threaded
   * this through yet keep today's behavior; the backend is the real
   * enforcement point either way.
   */
  canManage?: boolean;
  onSelectSprint?: (sprintId: string) => void;
  onCompleteClick?: () => void;
  onViewInsights?: () => void;
}

const SprintHeader: React.FC<SprintHeaderProps> = ({
  sprint,
  allSprints = [],
  canManage = true,
  onSelectSprint,
  onCompleteClick,
  onViewInsights,
}) => {
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const reportError = (fallbackMessage: string, error: any) => {
    dispatch(
      showToast({
        message: (typeof error === 'string' ? error : error?.message) || fallbackMessage,
        type: 'error',
      })
    );
  };

  const handleStartSprint = async () => {
    setLoading(true);
    try {
      await dispatch(startSprint(sprint.id)).unwrap();
    } catch (error) {
      console.error('Failed to start sprint:', error);
      reportError('Failed to start sprint', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSprint = async () => {
    setLoading(true);
    handleMenuClose();
    try {
      await dispatch(resetSprint(sprint.id)).unwrap();
    } catch (error) {
      console.error('Failed to reset sprint:', error);
      reportError('Failed to reset sprint', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSprint = () => {
    handleMenuClose();
    if (onCompleteClick) {
      onCompleteClick();
    }
  };

  const handleDeleteSprint = async () => {
    handleMenuClose();
    if (window.confirm(`Are you sure you want to delete "${sprint.name}"? Tasks will return to the backlog.`)) {
      setLoading(true);
      try {
        await dispatch(deleteSprint(sprint.id)).unwrap();
        if (sprint.boardId) {
          dispatch(fetchSprints(String(sprint.boardId)));
          dispatch(fetchTasks({ boardId: String(sprint.boardId) }));
        }
      } catch (error) {
        console.error('Failed to delete sprint:', error);
        reportError('Failed to delete sprint', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const calculateProgress = (): number => {
    if (!sprint.totalStoryPoints || sprint.totalStoryPoints === 0) {
      return 0;
    }
    return (
      ((sprint.completedStoryPoints || 0) / sprint.totalStoryPoints) * 100
    );
  };

  const calculateDaysRemaining = (): number => {
    const end = dayjs(sprint.endDate);
    const now = dayjs();
    return end.diff(now, 'day');
  };

  const getStatusColor = (): 'default' | 'primary' | 'success' | 'error' => {
    switch (sprint.status) {
      case 'ACTIVE':
        return 'success';
      case 'COMPLETED':
        return 'primary';
      case 'NOT_STARTED':
      default:
        return 'default';
    }
  };

  const statusUpper = (sprint.status || '').toUpperCase();
  const isSprintActive = statusUpper === 'ACTIVE' || sprint.isActive === true;
  const isSprintCompleted = statusUpper === 'COMPLETED';

  const daysRemaining = calculateDaysRemaining();
  const progress = calculateProgress();

  return (
    <Box className="sprint-header">
      <Box className="sprint-header-top">
        <Box className="sprint-info">
          {/* Active Indicator (Green Light) */}
          {isSprintActive && (
            <Tooltip title="Active Sprint" arrow>
              <Box className="active-indicator">
                <div className="pulse-dot" />
              </Box>
            </Tooltip>
          )}

          {/* Sprint Icon and Name */}
          <FlagIcon className="sprint-icon" color="primary" />
          
          {allSprints.length > 1 && onSelectSprint ? (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={sprint.id}
                onChange={(e) => onSelectSprint(e.target.value as string)}
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
              >
                {allSprints.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} ({ (s.status || 'PLANNING').replace('_', ' ') })
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="h5" className="sprint-name">
              {sprint.name}
            </Typography>
          )}

          {/* Status Chip */}
          <Chip
            label={(sprint.status || 'PLANNING').replace('_', ' ')}
            color={getStatusColor()}
            size="small"
            className="status-chip"
          />
        </Box>

        <Box className="sprint-actions">
          {/* Start/Complete/Reset Buttons - management-only */}
          {canManage && !isSprintActive && !isSprintCompleted && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartSprint}
              disabled={loading}
              className="action-button"
            >
              Start Sprint
            </Button>
          )}

          {isSprintActive && (
            <>
              {canManage && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleCompleteSprint}
                  disabled={loading}
                  className="action-button"
                >
                  Complete Sprint
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<TimelineIcon />}
                onClick={onViewInsights}
                className="action-button"
              >
                View Insights
              </Button>
            </>
          )}

          {isSprintCompleted && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Sprint Completed"
              color="success"
              variant="outlined"
              className="completed-chip"
            />
          )}

          {/* More Options Menu - Reset/Delete are management-only */}
          {canManage && (
            <>
              <IconButton onClick={handleMenuOpen} className="menu-button">
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                className="sprint-menu"
              >
                {sprint.status === 'ACTIVE' && (
                  <MenuItem onClick={handleResetSprint}>
                    <RestartAltIcon fontSize="small" />
                    <span>Reset Sprint</span>
                  </MenuItem>
                )}
                <MenuItem onClick={handleDeleteSprint} sx={{ color: '#d32f2f' }}>
                  <DeleteIcon fontSize="small" sx={{ mr: 1, color: '#d32f2f' }} />
                  <span>Delete Sprint</span>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Sprint Details Row */}
      <Box className="sprint-details">
        <Box className="detail-item">
          <CalendarTodayIcon fontSize="small" className="detail-icon" />
          <Typography variant="body2">
            {dayjs(sprint.startDate).format('MMM D')} -{' '}
            {dayjs(sprint.endDate).format('MMM D, YYYY')}
          </Typography>
        </Box>

        {sprint.status === 'ACTIVE' && (
          <>
            <Box className="detail-item">
              <Typography
                variant="body2"
                className={daysRemaining < 3 ? 'days-warning' : ''}
              >
                {daysRemaining > 0
                  ? `${daysRemaining} days remaining`
                  : daysRemaining === 0
                  ? 'Last day'
                  : 'Overdue'}
              </Typography>
            </Box>

            {sprint.totalStoryPoints && sprint.totalStoryPoints > 0 && (
              <Box className="detail-item">
                <Typography variant="body2">
                  {sprint.completedStoryPoints || 0} / {sprint.totalStoryPoints}{' '}
                  points completed
                </Typography>
              </Box>
            )}

            {sprint.velocity && (
              <Box className="detail-item">
                <TimelineIcon fontSize="small" className="detail-icon" />
                <Typography variant="body2">
                  Velocity: {sprint.velocity} pts/sprint
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Progress Bar */}
      {sprint.status === 'ACTIVE' &&
        sprint.totalStoryPoints &&
        sprint.totalStoryPoints > 0 && (
          <Box className="sprint-progress">
            <LinearProgress
              variant="determinate"
              value={progress}
              className={`progress-bar ${
                progress >= 75
                  ? 'high-progress'
                  : progress >= 50
                  ? 'medium-progress'
                  : 'low-progress'
              }`}
            />
            <Typography variant="caption" className="progress-text">
              {progress.toFixed(0)}% Complete
            </Typography>
          </Box>
        )}

      {/* Sprint Goal */}
      {sprint.goal && (
        <Box className="sprint-goal">
          <Typography variant="subtitle2" className="goal-label">
            Sprint Goal:
          </Typography>
          <Typography variant="body2" className="goal-text">
            {sprint.goal}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SprintHeader;
